import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Node,
  Edge,
  ConnectionMode,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
} from '@xyflow/react';
import { GRID_CONFIG } from '../gridConfig';
import { getTotalGridWidth, getTotalGridHeight } from '../utils/layoutMath';
import { Map3RelativesBaseProps } from './Map3Relatives';
import { createMapNode } from './Map3Relatives';
import {
  createResultNodesFromRelativesMapData,
  createEdgesFromDependencies,
} from '../utils/reactFlowUtils';
import { useContainerResize } from '../hooks/useContainerResize';
import { useKeyboardHandler } from '../hooks/useKeyboardHandler';
import { useConnectHandler } from '../hooks/useConnectHandler';
import { useEdgesChangeHandler } from '../hooks/useEdgesChangeHandler';
import { useDependencyHandler } from '../hooks/useDependencyHandler';
import { useNodesChangeHandler } from '../hooks/useNodesChangeHandler';
import { Map3UISettings } from '../types';

import { ResultCircleNodeData, circleNodeTypes } from './ResultCircleNode';
import {
  DraggableResultCircleNodeData,
  draggableCircleNodeTypes,
} from './DraggableResultCircleNode';
import { MapNodeData, mapNodeTypes } from './MapNode';
import './Map3Relatives.css';
import { FullyCollapsedMapsGroup } from './FullyCollapsedMapsGroup';
import { ReactFlow } from '@xyflow/react';
import { MilestoneMapInterval } from '../../types';

const nodeTypes = {
  ...circleNodeTypes,
  ...draggableCircleNodeTypes,
  ...mapNodeTypes,
};

/**
 * Props for Map3RelativesSharedTimeline component
 */
interface Map3RelativesSharedTimelineProps extends Map3RelativesBaseProps {
  zoomLevel: MilestoneMapInterval;
}

export function Map3RelativesSharedTimeline({
  mainMilestoneMapUuid,
  relatedMilestoneMaps,
  allObjectives,
  dependencyRecords,
  children,
  queueResultDeadlineUpdate = () => {},
  queueDependencyAdd = () => {},
  queueDependencyRemove = () => {},
  settings = {},
  zoomLevel,
  objectiveDisplayMode = 'show_all',
  relativesMapsData,
  updateMapState,
  canEdit,
}: Map3RelativesSharedTimelineProps) {
  // Get display settings from settings (passed from wrapper which gets it from Redux)
  const toggleStatusCellDisplay = settings.toggleStatusCellDisplay ?? 'status_results2_comments';
  const showResultCircles = toggleStatusCellDisplay !== 'status';
  const enableResultCircleFill = toggleStatusCellDisplay === 'status_results2_comments';
  const showStatusCodes = settings.showStatusCodes ?? false;

  // Drag and connect mode state management
  const [dragAndConnectModeResultUuid, setDragAndConnectModeResultUuid] = useState<string | null>(
    null,
  );

  const handleEnterDragAndConnectMode = useCallback(
    (resultUuid: string) => {
      if (!canEdit) {
        console.warn(
          '[Map3RelativesSharedTimeline] Drag and connect mode denied: user does not have edit permissions',
        );
        return;
      }
      console.log(
        '[Map3RelativesSharedTimeline] Entering drag and connect mode for result:',
        resultUuid,
      );
      setDragAndConnectModeResultUuid(resultUuid);
    },
    [canEdit],
  );

  const handleExitDragAndConnectMode = useCallback((resultUuid: string) => {
    console.log(
      '[Map3RelativesSharedTimeline] Exiting drag and connect mode for result:',
      resultUuid,
    );
    setDragAndConnectModeResultUuid(null);
  }, []);

  // State Management
  const flowWrapper = useRef<HTMLDivElement>(null);
  const dimensions = useContainerResize(flowWrapper);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedEdges, setSelectedEdges] = useState<Edge[]>([]);

  // Hover state for thick lines
  const [hoveredThickLineUuid, setHoveredThickLineUuid] = useState<string | null>(null);

  // TODO: Review. Unclear all this Container checking / error handling does anything helpful.
  //const [isContainerStable, setIsContainerStable] = useState(false);
  const [isContainerStable, setIsContainerStable] = useState(true);

  // Wait for container to stabilize before rendering ReactFlow
  /*
  // TODO: Review. Unclear if this does anything helpful.
  useEffect(() => {
    if (dimensions.width > 0 && dimensions.height > 0) {
      const timer = setTimeout(() => {
        setIsContainerStable(true);
      }, 500); // Wait 500ms after dimensions are set
      return () => clearTimeout(timer);
    } else {
      setIsContainerStable(false);
    }
  }, [dimensions.width, dimensions.height]);
  // */

  const { addDependency, removeDependency, removeDependenciesByIds } = useDependencyHandler(
    dependencyRecords,
    queueDependencyAdd,
    queueDependencyRemove,
    canEdit,
  );

  // The main map will be displayed at the top. We will find and
  // display all dependencies for the main map.
  // Note: Validation moved after all hooks to comply with rules of hooks.
  // Check is performed before the return statement.
  const mainMap = relatedMilestoneMaps.find((m) => m.uuid === mainMilestoneMapUuid);

  // Calculate total grid width based on unified timeline
  // - Sum of objective column width and all interval column widths
  const totalGridWidth = useMemo(() => {
    return getTotalGridWidth(
      relativesMapsData.map((m) => ({ ...m, intervals: m.adjustedIntervals })),
      'sharedTimeline',
      zoomLevel,
    );
  }, [relativesMapsData, zoomLevel]);

  // Calculate total grid height (same as stacked view)
  // - Sum of header height and all map heights
  const totalGridHeight = useMemo(() => {
    return getTotalGridHeight(relativesMapsData);
  }, [relativesMapsData]);

  /**
   * Timeline Key Generation
   *
   * This key changes whenever the timeline structure changes (e.g., when maps are added/removed
   * or when their columns change). It's used to determine when to reset node positions.
   *
   * The key is a string representation of the timeline structure:
   * - Each map's columns are represented by their end dates joined with '|'
   * - Maps are separated by '||'
   *
   * Example for 2 maps:
   * Map 1: [Jan 1, Feb 1] -> "2025-01-01T00:00:00.000Z|2025-02-01T00:00:00.000Z"
   * Map 2: [Jan 1, Feb 1, Mar 1] -> "2025-01-01T00:00:00.000Z|2025-02-01T00:00:00.000Z|2025-03-01T00:00:00.000Z"
   * Final key: "2025-01-01T00:00:00.000Z|2025-02-01T00:00:00.000Z||2025-01-01T00:00:00.000Z|2025-02-01T00:00:00.000Z|2025-03-01T00:00:00.000Z"
   *
   * The key changes when:
   * - A map is added/removed (changes number of '||'-separated sections)
   * - A map's columns change (changes dates in that map's section)
   * - The order of maps changes (changes order of sections)
   *
   * The key does NOT change when:
   * - Other map properties change (titles, objectives, etc.)
   * - Node positions change
   * - Zoom level changes (handled separately)
   */
  const timelineKey = useMemo(() => {
    return relativesMapsData
      .map((m) => m.columnsInfo.map((col) => col.endDate.toISOString()).join('|'))
      .join('||');
  }, [relativesMapsData]);

  // Node positioning in unified timeline
  // - Base positions come from createMapNode and result nodes as children
  // - mapXOffset comes from shared timeline column calculations to align all maps
  // - mapYOffset comes from stacking calculations to avoid map overlap
  // - Only recalculate nodes when zoomLevel, relativesMapsData, or timeline (timelineKey) changes
  useEffect(() => {
    // Create MapNodes and their child result nodes using sub-flows pattern
    const allNodes: Node[] = [];

    // First pass: collect fullyCollapsed maps for thick line rendering
    const fullyCollapsedMaps = relativesMapsData.filter(
      (relative) => relative.collapseLevel === 'fullyCollapsed',
    );

    relativesMapsData.forEach((relative, i) => {
      if (relative.collapseLevel === 'fullyCollapsed') {
        // For fullyCollapsed maps, only create result nodes for connections
        // The thick lines are rendered externally via FullyCollapsedRendering component
        const fullyCollapsedIndex = fullyCollapsedMaps.findIndex(
          (collapsed) => collapsed.milestoneMap.uuid === relative.milestoneMap.uuid,
        );
        const resultNodes = createResultNodesFromRelativesMapData(
          relative,
          enableResultCircleFill,
          dragAndConnectModeResultUuid,
          handleEnterDragAndConnectMode,
          handleExitDragAndConnectMode,
          showStatusCodes,
          fullyCollapsedIndex,
          fullyCollapsedMaps.length,
        );
        allNodes.push(...resultNodes);
      } else {
        // Create regular MapNode (parent container) for expanded/titleOnly maps
        const mapNode = createMapNode(
          relative,
          settings,
          zoomLevel,
          updateMapState,
          mainMilestoneMapUuid,
        );
        allNodes.push(mapNode);

        // Create result nodes (always circle nodes now)
        const resultNodes = createResultNodesFromRelativesMapData(
          relative,
          enableResultCircleFill,
          dragAndConnectModeResultUuid,
          handleEnterDragAndConnectMode,
          handleExitDragAndConnectMode,
          showStatusCodes,
          0, // fullyCollapsedIndex (not used for expanded/titleOnly)
          1, // totalFullyCollapsedMaps (not used for expanded/titleOnly)
        );
        allNodes.push(...resultNodes);
      }
    });

    // Reset node positions if zoom level or timeline has changed
    // otherwise preserve positions for smooth transitions.
    setNodes((prevNodes) => {
      // If zoom level or timeline changed, use new positions. (Timeline can change if user changes
      // visibility of maps which change the number of visible columns required to display all the
      // visible maps.)
      if (
        prevNodes.length === 0 ||
        (
          prevNodes.find(
            (n) => n.type === 'resultCircleNode' || n.type === 'draggableResultCircleNode',
          )?.data as any
        )?.zoomLevel !== zoomLevel ||
        (
          prevNodes.find(
            (n) => n.type === 'resultCircleNode' || n.type === 'draggableResultCircleNode',
          )?.data as any
        )?.timelineKey !== timelineKey
      ) {
        return allNodes.map((node) => {
          if (
            // resultNode removed - only circle nodes now
            node.type === 'resultCircleNode' ||
            node.type === 'draggableResultCircleNode'
          ) {
            return {
              ...node,
              data: {
                ...node.data,
                timelineKey,
                zoomLevel, // ✅ Fix: Also set zoomLevel to match the check above
              },
            };
          }
          return node;
        });
      }

      // Otherwise preserve positions for smooth transitions
      const prevNodeMap = new Map(prevNodes.map((n) => [n.id, n]));
      return allNodes.map((node) => {
        const prev = prevNodeMap.get(node.id);

        // For MapNodes, always use new position (they shouldn't move)
        if (node.type === 'mapNode') {
          return node;
        }

        // For result nodes, check if collapse state has changed
        if (
          prev &&
          (node.type === 'resultCircleNode' || node.type === 'draggableResultCircleNode')
        ) {
          // Find the map this node belongs to
          const nodeObjectiveUuid = (
            node.data as ResultCircleNodeData | DraggableResultCircleNodeData
          ).objectiveUuid;
          const nodeMap = relativesMapsData.find((m) =>
            m.objectives.some((obj) => obj.uuid === nodeObjectiveUuid),
          );

          // Find the previous map state if available
          const prevMapData = prevNodeMap.get(`map-${nodeMap?.milestoneMap.uuid}`);
          const prevCollapseLevel = prevMapData?.data?.collapseLevel;
          const currentCollapseLevel = nodeMap?.collapseLevel;

          // If collapse state changed, use new position; otherwise preserve old position
          if (prevCollapseLevel !== currentCollapseLevel) {
            return node; // Use new position when collapse state changes
          }

          return { ...node, position: prev.position }; // Preserve position when collapse state unchanged
        }

        return node; // New node, use new position
      });
    });
    // UPDATE: Including dragAndConnectModeResultUuid, handleEnterDragAndConnectMode, handleExitDragAndConnectMode,
    // and mainMilestoneMapUuid in the dependency array to trigger re-render when it changes
    // (toggling between ResultCircleNode and DraggableResultCircleNode).
    // Keep an eye on this in case re-renders go crazy after. -BH 2025-07-11
  }, [
    relativesMapsData,
    zoomLevel,
    timelineKey,
    settings,
    updateMapState,
    dragAndConnectModeResultUuid,
    handleEnterDragAndConnectMode,
    handleExitDragAndConnectMode,
    mainMilestoneMapUuid,
    enableResultCircleFill,
    showStatusCodes,
  ]);

  // Use the shared node change handler
  const onNodesChange = useNodesChangeHandler(
    relativesMapsData,
    setNodes,
    'sharedTimeline',
    queueResultDeadlineUpdate, // This will now queue updates for throttled dispatch
  );

  // Update edges when nodes or dependency records change
  useEffect(() => {
    setEdges(createEdgesFromDependencies(nodes, selectedEdges, dependencyRecords));
  }, [nodes, selectedEdges, dependencyRecords]);

  // Handle keyboard events
  const onKeyDown = useKeyboardHandler(
    selectedEdges,
    removeDependenciesByIds,
    setSelectedEdges,
    dragAndConnectModeResultUuid,
    handleExitDragAndConnectMode,
  );

  // Add onConnect handler for creating new dependencies
  const onConnect = useConnectHandler(nodes, dependencyRecords, addDependency);

  // Remove dependency on edge delete (selection + delete key)
  const onEdgesChange = useEdgesChangeHandler(edges, setSelectedEdges, removeDependenciesByIds);

  // Defensive guard: return null if mainMap not found (after all hooks)
  // This can happen during initial render race condition
  if (!mainMap) {
    console.warn(
      `[Map3RelativesSharedTimeline] Main map not found. uuid: ${mainMilestoneMapUuid}, available maps: ${relatedMilestoneMaps.length}`,
    );
    return null;
  }

  return (
    <ReactFlowProvider>
      <div
        ref={flowWrapper}
        className="relative w-full"
        style={{
          width: totalGridWidth,
          height: totalGridHeight,
          background: '#f4f4f5',
        }}
        tabIndex={0}
        onKeyDown={onKeyDown}
      >
        {children}

        {isContainerStable && dimensions.width > 0 && dimensions.height > 0 && (
          <div
            style={{
              width: dimensions.width,
              height: dimensions.height,
              position: 'relative',
              zIndex: GRID_CONFIG.zIndex.grid,
            }}
          >
            {/* 
              RENDERING OUTSIDE REACT FLOW STRATEGY - SHARED TIMELINE VERSION
              ===============================================================
              
              See detailed architectural comments in the helper components above.
            */}
            <FullyCollapsedMapsGroup
              fullyCollapsedMaps={relativesMapsData.filter(
                (relative) => relative.collapseLevel === 'fullyCollapsed',
              )}
              hoveredThickLineUuid={hoveredThickLineUuid}
              setHoveredThickLineUuid={setHoveredThickLineUuid}
              updateMapState={updateMapState}
              variant="sharedTimeline"
            />

            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              nodesDraggable={true}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              connectionMode={ConnectionMode.Loose}
              fitView={false}
              minZoom={1}
              maxZoom={1}
              preventScrolling={false}
              zoomOnScroll={false}
              panOnScroll={false}
              elementsSelectable={true}
              nodesFocusable={false}
              edgesFocusable={true}
              disableKeyboardA11y={false}
              panOnDrag={false}
              snapToGrid={false}
              snapGrid={[GRID_CONFIG.cellWidth, GRID_CONFIG.cellHeight]}
              className="react-flow-no-edge-animations"
              style={
                {
                  background: 'white',
                  '--xy-edge-stroke-default': '#374151',
                  '--xy-edge-stroke-width-default': '2px',
                } as React.CSSProperties
              }
              translateExtent={[
                [0, 0],
                [dimensions.width, dimensions.height],
              ]}
              //key={`flow-${zoomLevel}-${isContainerStable}`}
              key={`flow-${zoomLevel}-${objectiveDisplayMode}`}
            >
              {/* MapNodes now handle the visual representation, no need for separate overlays */}

              {/* Master grid using ReactFlow's Background component */}
              <Background
                gap={[GRID_CONFIG.cellWidth, GRID_CONFIG.cellHeight]}
                offset={[
                  GRID_CONFIG.objectiveWidth - GRID_CONFIG.cellWidth / 2,
                  GRID_CONFIG.headerHeight - GRID_CONFIG.cellHeight / 2,
                ]}
                color={GRID_CONFIG.colors.border}
                variant={BackgroundVariant.Lines}
                style={{ zIndex: 0 }}
              />
            </ReactFlow>
          </div>
        )}
      </div>
    </ReactFlowProvider>
  );
}
